import { useCallback, useEffect, useRef, useState } from "react";
import { adminClient } from "@/lib/api";
import {
  enqueue,
  flushQueue,
  loadCachedEvents,
  loadQueue,
  mergeEvents,
  newId,
  saveCachedEvents,
} from "@/lib/dvStore";

// Offline-first event store for one match. Every action is saved locally first, then synced.
export function useMatchEvents(matchId) {
  const [events, setEvents] = useState(() => mergeEvents(loadCachedEvents(matchId), loadQueue(matchId)));
  const [pending, setPending] = useState(() => loadQueue(matchId).length);
  const [online, setOnline] = useState(navigator.onLine);
  const history = useRef([]);
  const timer = useRef(null);

  const refreshPending = useCallback(() => setPending(loadQueue(matchId).length), [matchId]);

  const sync = useCallback(async () => {
    await flushQueue(matchId);
    refreshPending();
  }, [matchId, refreshPending]);

  const scheduleSync = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(sync, 250);
  }, [sync]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await flushQueue(matchId);
      try {
        const { data } = await adminClient.get(`/datavolley/matches/${matchId}`);
        if (cancelled) return;
        const merged = mergeEvents(data.events, loadQueue(matchId));
        setEvents(merged);
        saveCachedEvents(matchId, merged);
      } catch {
        /* offline: keep cache */
      }
      refreshPending();
    })();
    const goOnline = () => {
      setOnline(true);
      sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const interval = setInterval(sync, 15000);
    return () => {
      cancelled = true;
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);
      clearTimeout(timer.current);
    };
  }, [matchId, sync, refreshPending]);

  const record = useCallback(
    ({ set_number, player_id, category, type, delta }, track = true) => {
      const ev = { id: newId(), set_number, player_id, category, type, delta, ts: new Date().toISOString() };
      setEvents((prev) => {
        const next = [...prev, ev];
        saveCachedEvents(matchId, next);
        return next;
      });
      enqueue(matchId, ev);
      if (track) history.current.push(ev);
      refreshPending();
      scheduleSync();
      return ev;
    },
    [matchId, refreshPending, scheduleSync]
  );

  const undo = useCallback(() => {
    const last = history.current.pop();
    if (!last) return null;
    record({ ...last, delta: -last.delta }, false);
    return last;
  }, [record]);

  const resetSet = useCallback(
    async (setNumber) => {
      await flushQueue(matchId);
      await adminClient.delete(`/datavolley/matches/${matchId}/sets/${setNumber}/events`);
      setEvents((prev) => {
        const next = prev.filter((e) => e.set_number !== setNumber);
        saveCachedEvents(matchId, next);
        return next;
      });
      history.current = history.current.filter((e) => e.set_number !== setNumber);
      refreshPending();
    },
    [matchId, refreshPending]
  );

  return { events, record, undo, resetSet, pending, online, canUndo: history.current.length > 0 };
}
